import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { crossPortalService, CrossPortalAttachmentDto, UploadGlobalAttachmentDto } from '../services/CrossPortalService';
import { notifications } from '@mantine/notifications';

/**
 * Hook per ottenere gli allegati globali di una richiesta.
 * @param requestId - L'ID della richiesta in Ufficio Stile (ufficioStileRequestId).
 *                    Il backend recupererà automaticamente anche gli allegati tramite crossPortalRequestId.
 */
export const useGlobalAttachments = (requestId: number) => {
  return useQuery({
    queryKey: ['cross-portal', 'attachments', 'global', requestId],
    queryFn: () => crossPortalService.getGlobalAttachments(requestId),
    enabled: !!requestId && requestId > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUploadGlobalAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UploadGlobalAttachmentDto) => crossPortalService.uploadGlobalAttachment(data),
    onSuccess: (_data, variables) => {
      const label = variables.isGlobal ? 'globale' : 'locale';
      notifications.show({
        title: 'Upload completato',
        message: `File "${variables.file.name}" caricato con successo come allegato ${label}`,
        color: 'green',
      });

      queryClient.invalidateQueries({
        queryKey: ['cross-portal', 'attachments', 'global', variables.ufficioStileRequestId],
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Errore upload',
        message: error.message || 'Errore durante il caricamento del file',
        color: 'red',
      });
    },
  });
};

export const useDeleteGlobalAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; requestId: number }) => crossPortalService.deleteGlobalAttachment(id),
    onSuccess: (_data, variables) => {
      notifications.show({
        title: 'Allegato eliminato',
        message: 'Allegato eliminato con successo',
        color: 'green',
      });

      queryClient.invalidateQueries({
        queryKey: ['cross-portal', 'attachments', 'global', variables.requestId],
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Errore eliminazione',
        message: error.message || 'Errore durante l\'eliminazione dell\'allegato',
        color: 'red',
      });
    },
  });
};

export const useDownloadGlobalAttachment = () => {
  return useMutation({
    // Pass the attachment object, not just the id
    mutationFn: async (attachment: { id: string, fileName: string }) => {
      const downloadDto = await crossPortalService.getAttachmentDownloadUrl(attachment.id, attachment.fileName);
      // Open URL in new window - backend will redirect to Azure with SAS token
      window.open(downloadDto.url, '_blank');
      return downloadDto;
    },
    onSuccess: (data) => {
      notifications.show({
        title: 'Download avviato',
        message: `Download di "${data.fileName}" in corso`,
        color: 'blue',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Errore download',
        message: error.message || 'Errore durante il download del file',
        color: 'red',
      });
    },
  });
};